/**
 * ServiceDependencyMap — Interactive SVG dependency graph
 * Uses wol-* pattern for header / container to match the rest of the CMDB.
 * Draggable nodes, layered BFS layout, side info panel.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const _css = v => getComputedStyle(document.documentElement).getPropertyValue(v).trim();

const STATUS_KEYS = ['running', 'stopped', 'maintenance', 'degraded', 'unknown'];

const CRITICALITY_RING = {
  critical: '--crit-critical',
  high:     '--crit-high',
  medium:   '--crit-medium',
  low:      '--crit-low',
};

const NODE_W = 160;
const NODE_H = 52;
const PAD    = 40;

function layout(nodes, edges) {
  const adj = {};
  const indeg = {};
  nodes.forEach(n => { adj[n.id] = []; indeg[n.id] = 0; });
  edges.forEach(e => {
    if (adj[e.from] !== undefined) adj[e.from].push(e.to);
    if (indeg[e.to] !== undefined) indeg[e.to]++;
  });

  const depth = {};
  const queue = nodes.filter(n => indeg[n.id] === 0).map(n => n.id);
  queue.forEach(id => (depth[id] = 0));
  let qi = 0;
  while (qi < queue.length) {
    const id = queue[qi++];
    (adj[id] || []).forEach(nid => {
      if (depth[nid] === undefined || depth[nid] < depth[id] + 1) {
        depth[nid] = depth[id] + 1;
        queue.push(nid);
      }
    });
  }
  nodes.forEach(n => { if (depth[n.id] === undefined) depth[n.id] = 0; });

  const layers = {};
  nodes.forEach(n => {
    const d = depth[n.id];
    if (!layers[d]) layers[d] = [];
    layers[d].push(n.id);
  });

  const pos = {};
  Object.entries(layers).forEach(([d, ids]) => {
    const x = PAD + parseInt(d) * (NODE_W + 100);
    ids.forEach((id, i) => {
      pos[id] = { x, y: PAD + i * (NODE_H + 36) };
    });
  });

  return pos;
}

export default function ServiceDependencyMap({ companyId }) {
  const [graph, setGraph]     = useState({ nodes: [], edges: [] });
  const [pos, setPos]         = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(null);
  const svgRef = useRef(null);
  const posRef = useRef({});

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const g = await api.getServicesGraph(companyId);
      setGraph(g || { nodes: [], edges: [] });
      const p = layout(g.nodes || [], g.edges || []);
      setPos(p);
      posRef.current = p;
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const posVals = Object.values(pos);
  const svgW = Math.max(900, ...posVals.map(p => p.x + NODE_W + PAD * 2));
  const svgH = Math.max(500, ...posVals.map(p => p.y + NODE_H + PAD * 2));

  const startDrag = (e, id) => {
    e.stopPropagation();
    const origin = { x: e.clientX, y: e.clientY };
    const startPos = { ...posRef.current[id] };
    const onMove = (me) => {
      const np = {
        x: startPos.x + (me.clientX - origin.x),
        y: startPos.y + (me.clientY - origin.y),
      };
      posRef.current = { ...posRef.current, [id]: np };
      setPos(prev => ({ ...prev, [id]: np }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDragging(null);
    };
    setDragging(id);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const edgePath = (e) => {
    const from = pos[e.from];
    const to   = pos[e.to];
    if (!from || !to) return '';
    const x1 = from.x + NODE_W;
    const y1 = from.y + NODE_H / 2;
    const x2 = to.x;
    const y2 = to.y + NODE_H / 2;
    const mx = (x1 + x2) / 2;
    return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
  };

  const selNode = selected ? graph.nodes.find(n => n.id === selected) : null;

  return (
    <div className="wol-container" style={{ maxWidth: 'none', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="wol-header" style={{ padding: '1rem 1.5rem', margin: 0 }}>
        <div className="wol-header-left">
          <MapIcon className="wol-icon" />
          <div>
            <h1 className="wol-title">Mapa de dependencias</h1>
            <p className="wol-subtitle">{graph.nodes.length} servicios · {graph.edges.length} dependencias</p>
          </div>
        </div>
        <button className="wol-btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Cargando...' : '↺ Recargar'}
        </button>
      </div>

      {/* Canvas + side panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', borderTop: '1px solid var(--border)' }}>
        {/* SVG viewport */}
        <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-toolbar)' }}>
          {loading ? (
            <div className="wol-loading" style={{ height: '100%' }}>
              <div className="spinner-border text-primary" aria-hidden="true"></div>
              <span>Cargando mapa...</span>
            </div>
          ) : graph.nodes.length === 0 ? (
            <div className="wol-empty" style={{ height: '100%' }}>
              <span>🗺️</span>
              <p className="empty-title">No hay servicios para mostrar</p>
              <p className="empty-subtitle">Crea servicios y sus dependencias para ver el mapa.</p>
            </div>
          ) : (
            <svg
              ref={svgRef}
              width={svgW}
              height={svgH}
              style={{ cursor: dragging ? 'grabbing' : 'default', display: 'block' }}
            >
              <defs>
                <marker id="svc-arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L8,3 z" fill="var(--text-muted)" />
                </marker>
              </defs>

              {/* Edges */}
              {graph.edges.map((e, i) => {
                const isHighlight = selected && (e.from === selected || e.to === selected);
                const isDimmed = selected && !isHighlight;
                return (
                  <path
                    key={i}
                    d={edgePath(e)}
                    fill="none"
                    stroke={isHighlight ? _css('--accent') : _css('--edge-normal')}
                    strokeWidth={isHighlight ? 2 : 1.5}
                    markerEnd="url(#svc-arrow)"
                    opacity={isDimmed ? 0.2 : 1}
                  />
                );
              })}

              {/* Nodes */}
              {graph.nodes.map(n => {
                const p = pos[n.id];
                if (!p) return null;
                const isSelected = selected === n.id;
                const isDimmed = selected && !isSelected &&
                  !graph.edges.some(e => (e.from === selected && e.to === n.id) || (e.to === selected && e.from === n.id));
                const statusColor = _css(`--status-${n.status}`) || _css('--status-unknown');
                const ringColor = _css(CRITICALITY_RING[n.criticality] || '--edge-normal');

                return (
                  <g
                    key={n.id}
                    transform={`translate(${p.x},${p.y})`}
                    style={{ cursor: 'grab' }}
                    onMouseDown={ev => startDrag(ev, n.id)}
                    onClick={() => setSelected(s => s === n.id ? null : n.id)}
                    opacity={isDimmed ? 0.35 : 1}
                  >
                    {/* Criticality ring */}
                    <rect
                      x={-2} y={-2}
                      width={NODE_W + 4} height={NODE_H + 4}
                      rx={10} ry={10}
                      fill="none"
                      stroke={isSelected ? _css('--accent') : ringColor}
                      strokeWidth={isSelected ? 2.5 : 1.2}
                      opacity={isSelected ? 1 : 0.6}
                    />
                    {/* Background */}
                    <rect
                      x={0} y={0}
                      width={NODE_W} height={NODE_H}
                      rx={8} ry={8}
                      fill={isSelected ? _css('--node-selected-bg') : _css('--bg-card')}
                      stroke={isSelected ? _css('--accent') : _css('--border')}
                      strokeWidth={1}
                    />
                    {/* Status dot */}
                    <circle cx={14} cy={NODE_H / 2} r={5} fill={statusColor} />
                    {/* Label */}
                    <text
                      x={26} y={NODE_H / 2 - 6}
                      fill={_css('--text-primary')}
                      fontSize={12}
                      fontWeight={600}
                      dominantBaseline="middle"
                    >
                      {(n.display_name || n.name || '').substring(0, 18)}
                    </text>
                    <text
                      x={26} y={NODE_H / 2 + 9}
                      fill="var(--text-muted)"
                      fontSize={10}
                      dominantBaseline="middle"
                    >
                      {(n.service_type_label || n.service_type || '').substring(0, 22)}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>

        {/* Side info panel */}
        {selNode && (
          <div style={{ width: 280, borderLeft: '1px solid var(--border)', background: 'var(--bg-card)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>{selNode.display_name || selNode.name}</h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{selNode.service_type_label || selNode.service_type}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              {selNode.vendor && <InfoRow label="Vendor" value={selNode.vendor} />}
              {selNode.version && <InfoRow label="Versión" value={selNode.version} />}
              {selNode.status && <InfoRow label="Estado" value={selNode.status_label || selNode.status} />}
              {selNode.criticality && <InfoRow label="Criticidad" value={selNode.criticality_label || selNode.criticality} />}
              {selNode.url && <InfoRow label="URL" value={selNode.url} isLink />}
              {selNode.owner_team && <InfoRow label="Equipo" value={selNode.owner_team} />}
            </div>
            <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
              Salientes: {graph.edges.filter(e => e.from === selNode.id).length} · Entrantes: {graph.edges.filter(e => e.to === selNode.id).length}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '8px 24px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-card)' }}>
        {STATUS_KEYS.map(k => (
          <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: `var(--status-${k})`, display: 'inline-block' }} />
            {k}
          </span>
        ))}
            <span className="sdm-hint" style={{ marginLeft: 'auto' }}>Click para seleccionar · Arrastrar para mover</span>
      </div>
    </div>
  );
}

function InfoRow({ label, value, isLink }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: 'var(--text-muted)', width: 70, flexShrink: 0 }}>{label}</span>
      {isLink ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{value}</a>
      ) : (
        <span style={{ color: 'var(--text-primary)', wordBreak: 'break-all' }}>{value}</span>
      )}
    </div>
  );
}
